/* eslint-disable react/no-danger */
import React, { useMemo } from 'react';

import { SmButton } from '@smiles/smiles-react-design-system';
import Mustache from 'mustache';

import { TPurchaseValues } from '@/context/PurchaseProvider';

import useEvent from '@/hooks/useEvent';
import useApi from '@/hooks/useApi';

import { TModalContent } from '@/services/requests/Liferay/types';
import { dispatchInteraction } from '@/services/requests/Interactions/helpers';
import { TVoucherItem } from '@/services/requests/Voucher/types';

import { clearHtmlTags } from '@/helpers/HtmlHelper/clearHtmlTags';
import { getUuid } from '@/helpers/StringHelper/uuid';

type TProps = {
  onBack: () => void;
  onClose?: () => void;
  liferayContent: TModalContent;
  reusable?: boolean;
  currentVoucher: TVoucherItem;
  purchaseValues: TPurchaseValues;
};

export const Rules = ({
  onBack,
  onClose,
  liferayContent,
  currentVoucher,
  purchaseValues,
}: TProps) => {
  const liferayFilled = useMemo<TModalContent>(() => {
    return JSON.parse(
      Mustache.render(JSON.stringify(liferayContent), {
        voucher: currentVoucher,
        purchaseValues,
      }),
    );
  }, [currentVoucher, liferayContent, purchaseValues]);
  const milesLabelText = clearHtmlTags(liferayFilled.milesLabel?.text ?? '');

  const getMilesValueStr = (valueText: string): string => {
    const numberStr = valueText.replace(/[^0-9.]+/g, '');
    const parsedNumberString = parseFloat(numberStr).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    });
    const newText = valueText.replace(/[0-9.]+/g, parsedNumberString);
    return newText;
  };

  const rules = [
    { ...liferayFilled.codeLabel, value: currentVoucher.voucherNumber },
    {
      ...liferayFilled.validityLabel,
      value: liferayFilled.validityValue?.text,
    },
    {
      ...liferayFilled.milesValueLabel,
      value: getMilesValueStr(liferayFilled.milesValue?.text || ''),
    },
    {
      ...liferayFilled.installmentLabel,
      value: liferayFilled.installmentValue?.text,
    },
    {
      ...liferayFilled.maxMilesLabel,
      value: `${currentVoucher.quantity
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} ${milesLabelText}`,
    },
    { ...liferayFilled.minMilesLabel, value: `1.000 ${milesLabelText}` },
    {
      ...liferayFilled.reusableLabel,
      value: currentVoucher.reusable
        ? liferayFilled.yesLabel?.text
        : liferayFilled.noLabel?.text,
    },
    {
      ...liferayFilled.availabilityLabel,
      value: liferayFilled.availabilityValue?.text,
    },
  ];

  const { ApiInteractions } = useApi();
  const event = useEvent();

  const handleBack = (): void => {
    dispatchInteraction(ApiInteractions, 'extra_mpv_rules_click_back', {
      recommendationId: event.data.recommendationId,
    });

    onBack();
  };

  const handleClose = (): void => {
    dispatchInteraction(ApiInteractions, 'extra_mpv_ruler_click_close', {
      recommendationId: event.data.recommendationId,
    });

    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="smls-b2c-modals__modal-content">
      {event.data.modal === 'rules' ? (
        <div className="smls-b2c-modals__button--close">
          <SmButton
            onClick={handleClose}
            isPill
            isOutlined
            icon={<span className="material-icons">close</span>}
          />
        </div>
      ) : (
        <div className="smls-b2c-modals__button--back">
          <SmButton
            onClick={handleBack}
            isPill
            isOutlined
            icon={<span className="material-icons">arrow_back_ios</span>}
          />
        </div>
      )}

      <div className="rules">
        {liferayFilled.title && (
          <div
            className="rules__title"
            dangerouslySetInnerHTML={{ __html: liferayFilled.title.text }}
          />
        )}
        <div className="rules__table">
          {rules.map(({ icon, text, value }) => (
            <div className="rules__line" key={getUuid()}>
              {icon && (
                <div className="rules__icon">
                  <img src={icon} alt={clearHtmlTags(text ?? '')} />
                </div>
              )}

              {text && (
                <div
                  className="rules__text"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              )}
              {value && (
                <div
                  className="rules__value"
                  dangerouslySetInnerHTML={{ __html: value }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
